import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { Selector, State } from "./types";
import { createSelector } from "./selector";
import { Assignable, Removable } from "@planzone/dto";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  tags: [],
  scope: undefined,
  scopeId: undefined,
};

function isElementTag(assignable: Assignable | Removable) {
  return (
    assignable === Assignable.TASK ||
    assignable === Assignable.LIST ||
    assignable === Assignable.MILESTONE ||
    assignable === Assignable.EVENT
  );
}

function handleRequest(
  state: State,
  { scope, scopeId }: Boundaries["retrieveProjectsElementsTags"]["request"],
): State {
  return {
    ...state,
    request: setRequestStateToPending(),
    scope,
    scopeId,
  };
}

function handleSuccess(
  state: State,
  tags: Boundaries["retrieveProjectsElementsTags"]["success"],
): State {
  return { ...state, request: setRequestStateToSuccess(), tags };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleAssignTagRequested(
  state: State,
  { tag, assignable }: Boundaries["assignTag"]["request"],
): State {
  const isDuplicate = !!state.tags.find(({ name }) => name === tag.name);

  return isElementTag(assignable)
    ? {
        ...state,
        tags: !isDuplicate ? [tag, ...state.tags] : state.tags,
      }
    : state;
}

function handleAssignTagSuccess(
  state: State,
  { tag, assignable }: Boundaries["assignTag"]["success"],
): State {
  const isDuplicate = !!state.tags.find(({ name }) => name === tag.name);

  return isElementTag(assignable)
    ? {
        ...state,
        tags: !isDuplicate ? [tag, ...state.tags] : state.tags,
      }
    : state;
}

function handleAssignTagFailure(
  state: State,
  { tag, assignable }: Boundaries["assignTag"]["failure"],
): State {
  return isElementTag(assignable)
    ? {
        ...state,
        tags: state.tags.filter(({ name }) => name !== tag.name),
      }
    : state;
}

function handleRemoveTagRequested(
  state: State,
  { tag, removable }: Boundaries["removeTag"]["request"],
): State {
  return isElementTag(removable)
    ? {
        ...state,
        tags: state.tags.filter(({ name }) => name !== tag.name),
      }
    : state;
}

function handleRemoveTagSuccess(
  state: State,
  { tag, removable }: Boundaries["removeTag"]["success"],
): State {
  return isElementTag(removable)
    ? {
        ...state,
        tags: state.tags.filter(({ name }) => name !== tag.name),
      }
    : state;
}

function handleRemoveTagFailure(
  state: State,
  { tag, removable }: Boundaries["removeTag"]["failure"],
): State {
  const isDuplicate = !!state.tags.find(({ name }) => name === tag.name);

  return isElementTag(removable)
    ? {
        ...state,
        tags: !isDuplicate ? [tag, ...state.tags] : state.tags,
      }
    : state;
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveProjectsElementsTagsRequested]: handleRequest,
  [events.retrieveProjectsElementsTagsSucceeded]: handleSuccess,
  [events.retrieveProjectsElementsTagsFailed]: handleFailure,
  [events.assignTagRequested]: handleAssignTagRequested,
  [events.assignTagSucceeded]: handleAssignTagSuccess,
  [events.assignTagFailed]: handleAssignTagFailure,
  [events.removeTagRequested]: handleRemoveTagRequested,
  [events.removeTagSucceeded]: handleRemoveTagSuccess,
  [events.removeTagFailed]: handleRemoveTagFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
